// src/store/courtSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import courtService from '../services/courtService';
// Thunk to fetch admin courts
export const fetchCourts = createAsyncThunk(
  'court/fetchCourtsAdmin',
  async (_, { rejectWithValue }) => {
    try {
      return await courtService.getAllCourts();
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Thunk to fetch public courts
export const fetchPublicCourts = createAsyncThunk(
  'court/fetchPublicCourts',
  async (_, { rejectWithValue }) => {
    try {
      return await courtService.getPublicCourts();
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Thunk to fetch site owner courts
export const fetchSiteOwnerCourts = createAsyncThunk(
  'court/fetchSiteOwnerCourts',
  async (_, { rejectWithValue }) => {
    try {
      return await courtService.getCourts();
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Thunk to add a new court
export const addCourt = createAsyncThunk(
  'court/addCourt',
  async (courtDto, { rejectWithValue }) => {
    try {
      return await courtService.addCourtForOwner(courtDto);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Thunk to update a court
export const updateCourt = createAsyncThunk(
  'court/updateCourt',
  async ({ courtId, courtDto }, { rejectWithValue }) => {
    try {
      return await courtService.updateCourtForOwner(courtId, courtDto);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Thunk to delete a court by ID
export const deleteCourtById = createAsyncThunk(
  'court/deleteCourtById',
  async (id, { rejectWithValue }) => {
    try {
      await courtService.deleteCourtByDbId(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Thunk to delete a court by courtId
export const deleteCourtByCourtId = createAsyncThunk(
  'court/deleteCourtByCourtId',
  async (courtId, { rejectWithValue }) => {
    try {
      await courtService.deleteCourtByCourtIdForOwner(courtId);
      return courtId;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Thunk to start court stream
export const startCourtStream = createAsyncThunk(
  'court/startCourtStream',
  async (streamRequest, { rejectWithValue }) => {
    try {
      return await courtService.startCourtStream(streamRequest);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Thunk to stop court stream
export const stopCourtStream = createAsyncThunk(
  'court/stopCourtStream',
  async (stopRequest, { rejectWithValue }) => {
    try {
      await courtService.stopCourtStream(stopRequest);
      return stopRequest.streamKey;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Thunk to get court by ID
export const getCourtById = createAsyncThunk(
  'court/getCourtById',
  async (id, { rejectWithValue }) => {
    try {
      return await courtService.getCourtByDbId(id);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Thunk to get court by courtId
export const getCourtByCourtId = createAsyncThunk(
  'court/getCourtByCourtId',
  async (courtId, { rejectWithValue }) => {
    try {
      return await courtService.getCourtByCourtIdForOwner(courtId);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const courtSlice = createSlice({
  name: 'court',
  initialState: {
    courts: [],
    currentCourt: null,
    loading: false,
    error: null,
    streaming: {
      activeStreams: [],
      loading: false,
      error: null
    }
  },
  reducers: {
    clearCourts: (state) => {
      state.courts = [];
      state.currentCourt = null;
      state.loading = false;
      state.error = null;
    },
    setCurrentCourt: (state, action) => {
      state.currentCourt = action.payload;
    },
    clearCurrentCourt: (state) => {
      state.currentCourt = null;
    },
    updateStreamingStatus: (state, action) => {
      const { streamKey, isActive } = action.payload;
      if (isActive) {
        if (!state.streaming.activeStreams.includes(streamKey)) {
          state.streaming.activeStreams.push(streamKey);
        }
      } else {
        state.streaming.activeStreams = state.streaming.activeStreams.filter(key => key !== streamKey);
      }
    },
    clearStreamingError: (state) => {
      state.streaming.error = null;
    }
  },
  extraReducers: (builder) => {
    // Fetch courts
    builder.addCase(fetchCourts.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchCourts.fulfilled, (state, action) => {
      state.loading = false;
      state.courts = action.payload;
      state.error = null;
    });
    builder.addCase(fetchCourts.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || 'Failed to load courts.';
    });

    // Fetch public courts
    builder.addCase(fetchPublicCourts.pending, (state) => {
      state.loading = true;
      state.error = null;
    });

    builder.addCase(fetchPublicCourts.fulfilled, (state, action) => {
      state.loading = false;
      state.courts = action.payload;
      state.error = null;
    });

    builder.addCase(fetchPublicCourts.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || 'Failed to load courts.';
    });

    // Fetch site owner courts
    builder.addCase(fetchSiteOwnerCourts.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchSiteOwnerCourts.fulfilled, (state, action) => {
      state.loading = false;
      state.courts = action.payload;
      state.error = null;
    });
    builder.addCase(fetchSiteOwnerCourts.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || 'Failed to load courts.';
    });

    // Add court
    builder.addCase(addCourt.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(addCourt.fulfilled, (state, action) => {
      state.loading = false;
      state.courts.push(action.payload);
      state.error = null;
    });
    builder.addCase(addCourt.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || 'Failed to add court.';
    });

    // Update court
    builder.addCase(updateCourt.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateCourt.fulfilled, (state, action) => {
      state.loading = false;
      const index = state.courts.findIndex(court => court.courtId === action.payload.courtId);
      if (index !== -1) {
        state.courts[index] = action.payload;
      }
      if (state.currentCourt && state.currentCourt.courtId === action.payload.courtId) {
        state.currentCourt = action.payload;
      }
      state.error = null;
    });
    builder.addCase(updateCourt.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || 'Failed to update court.';
    });

    // Delete court by ID
    builder.addCase(deleteCourtById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteCourtById.fulfilled, (state, action) => {
      state.loading = false;
      state.courts = state.courts.filter(court => court.id !== action.payload);
      if (state.currentCourt && state.currentCourt.id === action.payload) {
        state.currentCourt = null;
      }
      state.error = null;
    });
    builder.addCase(deleteCourtById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || 'Failed to delete court.';
    });

    // Delete court by courtId
    builder.addCase(deleteCourtByCourtId.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteCourtByCourtId.fulfilled, (state, action) => {
      state.loading = false;
      state.courts = state.courts.filter(court => court.courtId !== action.payload);
      if (state.currentCourt && state.currentCourt.courtId === action.payload) {
        state.currentCourt = null;
      }
      state.error = null;
    });
    builder.addCase(deleteCourtByCourtId.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || 'Failed to delete court.';
    });

    // Start court stream
    builder.addCase(startCourtStream.pending, (state) => {
      state.streaming.loading = true;
      state.streaming.error = null;
    });
    builder.addCase(startCourtStream.fulfilled, (state, action) => {
      state.streaming.loading = false;
      if (!state.streaming.activeStreams.includes(action.payload.courtId)) {
        state.streaming.activeStreams.push(action.payload.courtId);
      }
      state.streaming.error = null;
    });
    builder.addCase(startCourtStream.rejected, (state, action) => {
      state.streaming.loading = false;
      state.streaming.error = action.payload || 'Failed to start stream.';
    });

    // Stop court stream
    builder.addCase(stopCourtStream.pending, (state) => {
      state.streaming.loading = true;
      state.streaming.error = null;
    });
    builder.addCase(stopCourtStream.fulfilled, (state, action) => {
      state.streaming.loading = false;
      state.streaming.activeStreams = state.streaming.activeStreams.filter(key => key !== action.payload);
      state.streaming.error = null;
    });
    builder.addCase(stopCourtStream.rejected, (state, action) => {
      state.streaming.loading = false;
      state.streaming.error = action.payload || 'Failed to stop stream.';
    });

    // Get court by ID
    builder.addCase(getCourtById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getCourtById.fulfilled, (state, action) => {
      state.loading = false;
      state.currentCourt = action.payload;
      state.error = null;
    });
    builder.addCase(getCourtById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || 'Failed to get court.';
    });

    // Get court by courtId
    builder.addCase(getCourtByCourtId.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getCourtByCourtId.fulfilled, (state, action) => {
      state.loading = false;
      state.currentCourt = action.payload;
      state.error = null;
    });
    builder.addCase(getCourtByCourtId.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || 'Failed to get court.';
    });
  },
});

export const { 
  clearCourts, 
  setCurrentCourt, 
  clearCurrentCourt, 
  updateStreamingStatus, 
  clearStreamingError,
} = courtSlice.actions;

// Selector functions for easier state access
export const selectCourts = (state) => state.court.courts;
export const selectCurrentCourt = (state) => state.court.currentCourt;
export const selectCourtLoading = (state) => state.court.loading;
export const selectCourtError = (state) => state.court.error;
export const selectStreamingState = (state) => state.court.streaming;
export const selectActiveStreams = (state) => state.court.streaming.activeStreams;
export const selectIsStreamActive = (courtId) => (state) => 
  state.court.streaming.activeStreams.includes(courtId);

export default courtSlice.reducer;